import {FC, useEffect, useMemo, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import {api, ProviderPerformance} from '../../services/api'

type SortKey = keyof Pick<
  ProviderPerformance,
  'providerName' | 'category' | 'bookings' | 'revenue' | 'completed' | 'cancelled' | 'rating'
>
type SortDir = 'asc' | 'desc'

const fmtMoney = (n: number) => `$${(n ?? 0).toLocaleString()}`
const fmtNum = (n: number) => (n ?? 0).toLocaleString()

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const chartOptions = (names: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'bar', toolbar: {show: false}, fontFamily: 'inherit'},
  plotOptions: {bar: {borderRadius: 4, horizontal: true, barHeight: '60%'}},
  colors: ['#3b82f6'],
  dataLabels: {enabled: false},
  xaxis: {
    categories: names,
    labels: {formatter: (v) => `${(Number(v) / 1000).toFixed(0)}k`},
    axisBorder: {show: false},
    axisTicks: {show: false},
  },
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  tooltip: {y: {formatter: (v) => `$${v.toLocaleString()}`}},
})

const SortIcon: FC<{active: boolean; dir: SortDir}> = ({active, dir}) => (
  <span style={{marginLeft: 4, color: active ? '#3b82f6' : '#cbd5e1', fontSize: 11}}>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
)

const ProviderPerformancePage: FC = () => {
  const [data, setData] = useState<ProviderPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('revenue')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  useEffect(() => {
    let active = true
    api
      .getProviderPerformance(100)
      .then((rows) => {
        if (active) setData(rows || [])
      })
      .catch(() => {
        if (active) setData([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const hasRating = useMemo(() => data.some((d) => d.rating != null), [data])

  const totalRevenue = useMemo(() => data.reduce((sum, d) => sum + (d.revenue ?? 0), 0), [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (d) =>
        d.providerName?.toLowerCase().includes(q) || d.category?.toLowerCase().includes(q)
    )
  }, [data, search])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      let cmp: number
      if (typeof av === 'string' || typeof bv === 'string') {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''))
      } else {
        cmp = (Number(av) || 0) - (Number(bv) || 0)
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return rows
  }, [filtered, sortKey, sortDir])

  const topRevenue = useMemo(() => {
    return [...data]
      .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0))
      .slice(0, 10)
  }, [data])

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      // sensible default: text asc, numbers desc
      setSortDir(key === 'providerName' || key === 'category' ? 'asc' : 'desc')
    }
  }

  const headers: {key: SortKey; label: string; align: 'left' | 'right'; show?: boolean}[] = [
    {key: 'providerName', label: 'Provider', align: 'left'},
    {key: 'category', label: 'Category', align: 'left'},
    {key: 'bookings', label: 'Bookings', align: 'right'},
    {key: 'revenue', label: 'Revenue ($)', align: 'right'},
    {key: 'completed', label: 'Completed', align: 'right'},
    {key: 'cancelled', label: 'Cancelled', align: 'right'},
    {key: 'rating', label: 'Rating', align: 'right', show: hasRating},
  ]

  const thBase: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #f1f5f9',
  }
  const tdBase: React.CSSProperties = {
    padding: '14px 16px',
    fontSize: 14,
    color: '#0f172a',
    borderBottom: '1px solid #f1f5f9',
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>
          Provider Performance
        </h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Bookings, revenue and completion stats by service provider.
        </p>
      </div>

      {/* Summary */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 20}}>
        <div style={{...cardStyle, padding: '20px'}}>
          <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>
            {loading ? '—' : fmtNum(data.length)}
          </div>
          <div style={{fontSize: 13, color: '#94a3b8'}}>Total Providers</div>
        </div>
        <div style={{...cardStyle, padding: '20px'}}>
          <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>
            {loading ? '—' : fmtMoney(totalRevenue)}
          </div>
          <div style={{fontSize: 13, color: '#94a3b8'}}>Total Revenue</div>
        </div>
      </div>

      {/* Top-10 revenue chart */}
      {!loading && topRevenue.length > 0 && (
        <div style={{...cardStyle, padding: '20px', marginBottom: 20}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 16px'}}>
            Top Providers by Revenue
          </h3>
          <ReactApexChart
            type='bar'
            series={[{name: 'Revenue', data: topRevenue.map((p) => p.revenue ?? 0)}]}
            options={chartOptions(topRevenue.map((p) => p.providerName))}
            height={Math.max(240, topRevenue.length * 38)}
          />
        </div>
      )}

      {/* Table */}
      <div style={{...cardStyle, overflow: 'hidden'}}>
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>
            Providers{' '}
            {!loading && (
              <span style={{color: '#94a3b8', fontWeight: 500}}>({fmtNum(sorted.length)})</span>
            )}
          </h3>
          <input
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search provider or category…'
            style={{
              width: 260,
              maxWidth: '50%',
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              outline: 'none',
              color: '#0f172a',
            }}
          />
        </div>

        {loading ? (
          <div style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14}}>
            Loading provider performance…
          </div>
        ) : sorted.length === 0 ? (
          <div style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14}}>
            {data.length === 0
              ? 'No provider performance data available.'
              : 'No providers match your search.'}
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead>
                <tr>
                  {headers
                    .filter((h) => h.show !== false)
                    .map((h) => (
                      <th
                        key={h.key}
                        onClick={() => toggleSort(h.key)}
                        style={{...thBase, textAlign: h.align}}
                      >
                        {h.label}
                        <SortIcon active={sortKey === h.key} dir={sortDir} />
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.providerId}>
                    <td style={{...tdBase, fontWeight: 600}}>{p.providerName}</td>
                    <td style={tdBase}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 500,
                          background: '#f1f5f9',
                          color: '#475569',
                        }}
                      >
                        {p.category}
                      </span>
                    </td>
                    <td style={{...tdBase, textAlign: 'right'}}>{fmtNum(p.bookings)}</td>
                    <td style={{...tdBase, textAlign: 'right', fontWeight: 600}}>
                      {fmtMoney(p.revenue)}
                    </td>
                    <td style={{...tdBase, textAlign: 'right', color: '#16a34a'}}>
                      {fmtNum(p.completed)}
                    </td>
                    <td style={{...tdBase, textAlign: 'right', color: '#ef4444'}}>
                      {fmtNum(p.cancelled)}
                    </td>
                    {hasRating && (
                      <td style={{...tdBase, textAlign: 'right'}}>
                        {p.rating != null ? `★ ${p.rating.toFixed(1)}` : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

const ProviderPerformancePageWrapper: FC = () => <ProviderPerformancePage />

export {ProviderPerformancePageWrapper}
